/**
 * Enhanced Page Editor
 *
 * Complete page builder with toolbar, properties editing, save/load functionality,
 * and preview mode. Integrates all the missing functionality requested.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Alert, Snackbar, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { MinimalDragDropEditor } from './MinimalDragDropEditor';
import { PreviewRenderer } from './PreviewRenderer';
import { EditorToolbar } from './EditorToolbar';
import { ContentMetadataDialog } from '../content/ContentMetadataDialog';
import { PropertyPanel } from './PropertyPanel';
import { PageDoc, Block } from '../../types/page-builder.types';
import { ViewRenderProvider } from '../../context/ViewRenderContext';
import { ViewCategory, CreateViewRequest, UpdateViewRequest } from '../../types/view.types';
import { viewsApiService } from '../../services/views.api';
import { convertPageDocToMDX, convertMDXToPageDoc, extractMetadataFromMDX } from '../../utils/pageDoc-converter';
import { ComponentRegistry } from '../../utils/component-registry';

interface EditorState {
  isSaving: boolean;
  isLoading: boolean;
  isPreviewMode: boolean;
  hasUnsavedChanges: boolean;
  lastSaved?: Date;
  canUndo: boolean;
  canRedo: boolean;
  error?: string;
  success?: string;
}

interface ViewMetadata {
  title: string;
  description: string;
  category: ViewCategory;
  tagIds: string[];  // Unified tag system (references Tag entity IDs)
  isPublic: boolean; // Legacy support
  status?: 'draft' | 'private' | 'public' | 'archived'; // New unified status
  teamId?: string;
  organizationId?: string;
}

interface EnhancedPageEditorProps {
  initialPageDoc?: PageDoc;
  initialMetadata?: Partial<ViewMetadata>;
  viewId?: string; // For editing existing views
  teamId: string;
  organizationId: string;
  teamName?: string;
  organizationName?: string;
  onSaveSuccess?: (viewId: string) => void;
  onError?: (error: string) => void;
  readonly?: boolean;
}

export const EnhancedPageEditor: React.FC<EnhancedPageEditorProps> = ({
  initialPageDoc,
  initialMetadata,
  viewId,
  teamId,
  organizationId,
  teamName,
  organizationName,
  onSaveSuccess,
  onError,
  readonly = false
}) => {
  // Initialize PageDoc with full structure for editing
  // Note: Only the layout will be saved to storage, metadata goes to View entity
  const [pageDoc, setPageDoc] = useState<PageDoc>(
    initialPageDoc || {
      _id: `page-${Date.now()}`,
      orgId: organizationId,
      teamIds: teamId ? [teamId] : [],
      slug: 'untitled-page',
      status: 'draft',
      title: 'Untitled Page',
      description: '',
      version: 1,
      createdBy: 'current-user',
      updatedBy: 'current-user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      layout: [
        // Start with one section containing a row and column (matches MinimalDragDropEditor's createDefaultSection)
        {
          id: `section-${Date.now()}`,
          kind: 'container',
          rows: [
            {
              id: `row-${Date.now()}`,
              columns: [
                {
                  id: `col-${Date.now()}`,
                  width: 12,
                  blocks: [],
                  style: {
                    margin: { top: 0, right: 5, bottom: 0, left: 0 },
                    border: { width: 0, style: 'solid', color: '#e0e0e0' },
                    borderRadius: 0,
                    minHeight: 100
                  }
                }
              ],
              style: {
                margin: { top: 0, right: 0, bottom: 5, left: 0 },
                border: { width: 0, style: 'solid', color: '#e0e0e0' },
                borderRadius: 0,
              }
            }
          ],
          style: {
            margin: { top: 0, right: 0, bottom: 5, left: 0 },
            border: { width: 0, style: 'solid', color: '#e0e0e0' },
            borderRadius: 0,
          },
          visible: true
        }
      ],
    }
  );

  const [metadata, setMetadata] = useState<ViewMetadata>({
    title: initialMetadata?.title || pageDoc.title,
    description: initialMetadata?.description || pageDoc.description,
    category: initialMetadata?.category || 'custom',
    tagIds: initialMetadata?.tagIds || [],  // Unified tag system
    isPublic: initialMetadata?.isPublic || false,
    status: initialMetadata?.status || 'private',
    teamId: initialMetadata?.teamId || teamId,
    organizationId: initialMetadata?.organizationId || organizationId,
  });

  const [editorState, setEditorState] = useState<EditorState>({
    isSaving: false,
    isLoading: false,
    isPreviewMode: false,
    hasUnsavedChanges: false,
    canUndo: false,
    canRedo: false,
  });

  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | undefined>();
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Ref for tracking undo/redo operations
  const isUndoRedoRef = useRef(false);

  // Navigation
  const navigate = useNavigate();

  // History management (simplified)
  // CRITICAL: Deep clone initial pageDoc to prevent reference issues
  const [history, setHistory] = useState<{ past: PageDoc[], present: PageDoc, future: PageDoc[] }>(() => ({
    past: [],
    present: JSON.parse(JSON.stringify(initialPageDoc || pageDoc)),
    future: [],
  }));

  // Track if we've already created the initial view to prevent duplicates
  const initialViewCreatedRef = useRef(false);
  const previousViewIdRef = useRef<string | undefined>(viewId);

  // Load existing view if viewId is provided and this is truly an edit (not a create->edit transition)
  useEffect(() => {
    // Only load if:
    // 1. We have a viewId
    // 2. No initial PageDoc was provided
    // 3. This isn't a transition from create mode (viewId changing from undefined to defined)
    const isCreateToEditTransition = !previousViewIdRef.current && viewId && initialViewCreatedRef.current;

    if (viewId && !initialPageDoc && !isCreateToEditTransition) {
      loadView(viewId);
    }

    // Update previous viewId
    previousViewIdRef.current = viewId;
  }, [viewId]);

  // Create initial view with default metadata when in create mode
  // This ensures auto-save works from the start and prevents duplicate creation
  useEffect(() => {
    // Only create if:
    // 1. We're in create mode (no viewId)
    // 2. Have valid metadata
    // 3. Haven't already created the initial view
    // 4. Not currently loading or saving
    if (!viewId &&
        metadata.title &&
        !initialViewCreatedRef.current &&
        !editorState.isLoading &&
        !editorState.isSaving) {
      initialViewCreatedRef.current = true;
      createInitialView();
    }
  }, []); // Empty deps - only run on mount

  // SAVE STRATEGY: No auto-save on timer to prevent conflicts with undo/redo
  //
  // Changes are saved only when:
  // 1. Manual save (Cmd+S or Save button click)
  // 2. Before navigation (click interceptor shows "Save/Discard/Stay" dialog)
  // 3. Before page close/refresh (beforeunload event shows browser warning)
  // 4. Metadata/properties dialog save
  //
  // Navigation handling:
  // - Internal navigation: Click event listener intercepts all link clicks
  // - Shows custom dialog with three options: Save & Leave, Discard Changes, Stay Here
  // - Browser close/refresh: beforeunload shows browser's built-in warning
  // - User has full control over saving before navigation
  //
  // This ensures:
  // - Undo/redo stack is never invalidated by background saves
  // - No accidental data loss from clicking navigation links
  // - User has full control over when changes are persisted
  // - No race conditions between auto-save and undo/redo operations
  // - Clear dialog choices prevent confusion

  // Convert PageDoc to View format for API (moved here to be available for handleForceSave)
  const convertPageDocToView = (doc: PageDoc, meta: ViewMetadata): CreateViewRequest | UpdateViewRequest => {
    // For MDX generation, we need the full PageDoc with metadata
    const mdxContent = convertPageDocToMDX(doc);
    
    // Extract only the layout structure for storage (not metadata)
    // The metadata belongs in the View entity, not in PageDoc storage
    const cleanPageDoc = {
      layout: doc.layout,
      style: doc.style, // Include view-level styling
      frontmatter: doc.frontmatter // optional MDX metadata
    };
    
    // Map status to enum: draft=1, private=2, public=3, archived=4
    let statusEnum: number;
    if (meta.status) {
      // Use the status field if available
      switch (meta.status) {
        case 'draft': statusEnum = 1; break;
        case 'private': statusEnum = 2; break;
        case 'public': statusEnum = 3; break;
        case 'archived': statusEnum = 4; break;
        default: statusEnum = 2; // Default to private
      }
    } else {
      // Fallback to isPublic for backward compatibility
      statusEnum = meta.isPublic ? 3 : 2;
    }

    return {
      name: meta.title,
      description: meta.description,
      organization_id: organizationId,
      team_id: teamId,
      mdx_content: mdxContent,
      page_doc: JSON.stringify(cleanPageDoc),  // Only layout structure for storage
      status: statusEnum,
      tagIds: meta.tagIds,  // Unified tag system (references Tag entity IDs)
      category: meta.category,
    };
  };

  // Note: Cleanup effect removed - no auto-save timeout to clean up
  // Unsaved changes are handled by beforeunload event below


  // Validation function per spec: "property panel with schema‑driven forms; validation errors block publish"
  const validatePageDoc = (doc: PageDoc, meta: ViewMetadata): string[] => {
    const errors: string[] = [];

    // Basic metadata validation
    if (!meta.title.trim()) {
      errors.push('Title is required');
    }
    if (!meta.description.trim()) {
      errors.push('Description is required');
    }

    // PageDoc structure validation
    if (!doc.layout || doc.layout.length === 0) {
      errors.push('Page must have at least one section');
    }

    // Component validation using registry schemas
    for (const section of doc.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          for (const block of column.blocks) {
            try {
              const componentSchema = ComponentRegistry[block.type]?.schema;
              if (componentSchema) {
                componentSchema.parse(block.props);
              }
            } catch (validationError: any) {
              errors.push(`Invalid ${block.type}: ${validationError.message}`);
            }
          }
        }
      }
    }

    return errors;
  };

  const createInitialView = async () => {
    try {
      console.log('[CreateInitialView] Starting initial view creation...');
      setEditorState(prev => ({ ...prev, isSaving: true, error: undefined }));

      // Validate before creating
      const validationErrors = validatePageDoc(pageDoc, metadata);
      if (validationErrors.length > 0) {
        console.warn('[CreateInitialView] Validation errors:', validationErrors);
        setEditorState(prev => ({
          ...prev,
          isSaving: false,
          validationErrors,
        }));
        return;
      }

      const viewData = convertPageDocToView(pageDoc, metadata);
      console.log('[CreateInitialView] Creating view with data:', {
        name: viewData.name,
        hasLayout: !!viewData.page_doc
      });

      const savedView = await viewsApiService.createView(viewData as CreateViewRequest);
      console.log('[CreateInitialView] View created successfully:', savedView.id);

      setEditorState(prev => ({
        ...prev,
        isSaving: false,
        hasUnsavedChanges: false,
        lastSaved: new Date(),
        validationErrors: [],
      }));

      // Notify parent component of the new viewId so URL can be updated
      onSaveSuccess?.(savedView.id);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create initial view';
      console.error('[CreateInitialView] Error:', error);
      setEditorState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      onError?.(errorMessage);
    }
  };

  const loadView = async (id: string) => {
    try {
      setEditorState(prev => ({ ...prev, isLoading: true, error: undefined }));
      
      const view = await viewsApiService.getView(id, teamId);
      
      // Use saved PageDoc if available, otherwise convert from MDX
      let loadedPageDoc: PageDoc;
      
      if (view.pageDoc) {
        // Reconstruct full PageDoc from clean pageDoc + view metadata
        // The saved pageDoc only contains layout, we need to add back the metadata for editing
        const cleanPageDoc = typeof view.pageDoc === 'string' ? JSON.parse(view.pageDoc) : view.pageDoc;
        loadedPageDoc = {
          _id: view.id,
          orgId: organizationId,
          teamIds: teamId ? [teamId] : [],
          slug: view.name.toLowerCase().replace(/\s+/g, '-'),
          status: 'draft',
          title: view.name,
          description: view.description,
          version: view.version || 1,
          createdBy: view.createdBy,
          updatedBy: view.createdBy,
          createdAt: view.createdAt,
          updatedAt: view.updatedAt,
          layout: cleanPageDoc.layout || [],
          style: cleanPageDoc.style, // Include view-level styling
          frontmatter: cleanPageDoc.frontmatter
        };
      } else {
        // Fallback: Convert View back to PageDoc format from MDX
        loadedPageDoc = convertMDXToPageDoc(view.mdxContent || '', {
          _id: view.id,
          orgId: organizationId,
          teamIds: teamId ? [teamId] : [],
          slug: view.name.toLowerCase().replace(/\s+/g, '-'),
          status: 'draft',
          title: view.name,
          description: view.description,
          version: view.version || 1,
          createdBy: view.createdBy,
          updatedBy: view.createdBy,
          createdAt: view.createdAt,
          updatedAt: view.updatedAt,
        });
      }

      setPageDoc(loadedPageDoc);
      setMetadata({
        title: view.name,
        description: view.description,
        category: view.category,
        tagIds: view.tagIds || [],  // Unified tag system
        status: view.status, // Use the status field from the API
        isPublic: view.status === 'public', // Set isPublic for backward compatibility
        teamId: view.teamId,
        organizationId: view.organizationId,
      });

      setHistory({
        past: [],
        present: loadedPageDoc,
        future: [],
      });

      setEditorState(prev => ({
        ...prev,
        isLoading: false,
        hasUnsavedChanges: false,
        lastSaved: new Date(view.updatedAt),
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load view';
      setEditorState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
      onError?.(errorMessage);
    }
  };

  const handleSave = async () => {
    try {
      setEditorState(prev => ({ ...prev, isSaving: true, error: undefined }));

      // Validate before manual save per spec: "validation errors block publish"
      const validationErrors = validatePageDoc(pageDoc, metadata);
      if (validationErrors.length > 0) {
        setEditorState(prev => ({
          ...prev,
          isSaving: false,
          error: `Validation failed: ${validationErrors.join(', ')}`,
          validationErrors
        }));
        return;
      }

      const viewData = convertPageDocToView(pageDoc, metadata);
      let savedView;

      if (viewId) {
        // Update existing view
        savedView = await viewsApiService.updateView(viewId, viewData, teamId);
      } else {
        // Fallback: Create new view if somehow viewId is missing
        // This shouldn't normally happen since createInitialView runs on mount
        savedView = await viewsApiService.createView(viewData as CreateViewRequest);
      }

      // Don't modify history on save - just mark as saved
      setEditorState(prev => ({
        ...prev,
        isSaving: false,
        hasUnsavedChanges: false,
        lastSaved: new Date(),
        success: 'View saved successfully!',
      }));

      onSaveSuccess?.(savedView.id);

    } catch (error: any) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to save view';
      let errorDetails = '';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Extract additional details if available (cast to any to access custom properties)
        const errorWithDetails = error as any;
        if (errorWithDetails.details) {
          console.error('[Save Error Details]', errorWithDetails.details);
          
          // Build detailed error message for display
          if (errorWithDetails.details.code) {
            errorDetails = `Error Code: ${errorWithDetails.details.code}`;
          }
          if (errorWithDetails.details.serviceName) {
            errorDetails += errorDetails ? `, Service: ${errorWithDetails.details.serviceName}` : `Service: ${errorWithDetails.details.serviceName}`;
          }
          if (errorWithDetails.details.requestId) {
            console.error(`Request ID for debugging: ${errorWithDetails.details.requestId}`);
          }
          
          // Log the request payload for debugging
          if (errorWithDetails.details.requestPayload) {
            console.error('[Failed Request Payload]', JSON.parse(errorWithDetails.details.requestPayload));
          }
        }
      }
      
      const fullErrorMessage = errorDetails ? `${errorMessage} (${errorDetails})` : errorMessage;
      setEditorState(prev => ({ ...prev, isSaving: false, error: fullErrorMessage }));
      onError?.(fullErrorMessage);
    }
  };

  // Force immediate save (used on navigation)
  const handleForceSave = useCallback(() => {
    // Check current state - don't save if already saved or currently saving
    if (!metadata.title.trim() || editorState.isSaving || !editorState.hasUnsavedChanges) {
      return Promise.resolve();
    }

    // Validate PageDoc before saving
    const validationErrors = validatePageDoc(pageDoc, metadata);
    if (validationErrors.length > 0) {
      console.warn('Force save skipped due to validation errors:', validationErrors);
      return Promise.resolve();
    }

    const viewData = convertPageDocToView(pageDoc, metadata);

    // Return promise for synchronous handling
    return viewId
      ? viewsApiService.updateView(viewId, viewData, teamId)
      : viewsApiService.createView(viewData as CreateViewRequest);
  }, [metadata, editorState.isSaving, editorState.hasUnsavedChanges, pageDoc, viewId]);

  // Note: Auto-save function removed - see note at top of file for rationale

  // Warn user about unsaved changes when leaving the page (moved after handleForceSave definition)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (editorState.hasUnsavedChanges && !readonly) {
        // Try to force save before showing warning
        // This is best-effort - browser may not wait for async operations
        if (!editorState.isSaving) {
          handleForceSave()
            .then(() => {
              console.log('Successfully saved before navigation');
              // Update state to reflect save
              setEditorState(prev => ({ 
                ...prev, 
                hasUnsavedChanges: false,
                lastSaved: new Date()
              }));
            })
            .catch((error) => {
              console.error('Failed to save before navigation:', error);
            });
        }
        
        // Still show warning as save might not complete
        // Standard way to show browser's default warning
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editorState.hasUnsavedChanges, editorState.isSaving, readonly, handleForceSave]);

  // Intercept navigation attempts when there are unsaved changes
  useEffect(() => {
    if (!editorState.hasUnsavedChanges || readonly) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]');

      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('http')) {
        return; // Skip hash links and external links
      }

      // Check if this is an internal navigation link
      if (href.startsWith('/')) {
        e.preventDefault();
        setPendingNavigation(href);
        setShowNavigationDialog(true);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [editorState.hasUnsavedChanges, readonly]);

  // Navigation dialog handlers
  const handleSaveAndNavigate = async () => {
    try {
      await handleSave();
      setShowNavigationDialog(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Failed to save before navigation:', error);
      setShowNavigationDialog(false);
    }
  };

  const handleDiscardAndNavigate = () => {
    setShowNavigationDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  };

  const handlePageUpdate = useCallback((updatedDoc: PageDoc) => {
    // Update document with timestamp
    const updatedDocWithTime = {
      ...updatedDoc,
      updatedAt: new Date().toISOString()
    };

    setPageDoc(updatedDocWithTime);
    setMetadata(prev => ({
      ...prev,
      title: updatedDocWithTime.title,
      description: updatedDocWithTime.description,
    }));

    // Update history to track changes properly
    setHistory(prev => {
      // Check if this update is actually different from current state
      // Compare the layout structure (ignore timestamps)
      const currentLayout = JSON.stringify(prev.present.layout);
      const newLayout = JSON.stringify(updatedDocWithTime.layout);

      // If layout is identical, don't add to history (this prevents duplicate entries)
      if (currentLayout === newLayout) {
        return {
          ...prev,
          present: updatedDocWithTime, // Update timestamp but don't add to history
        };
      }

      // Add current state to past and update present
      return {
        past: [...prev.past, prev.present].slice(-20), // Keep last 20 for memory efficiency
        present: updatedDocWithTime,
        future: [] // Clear future when making a new change
      };
    });

    // Mark as having unsaved changes
    // Note: canUndo will be updated by the history effect below
    setEditorState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
      canRedo: false
    }));

    // Note: Auto-save removed - changes only saved on manual save or before navigation
  }, []);

  // Update canUndo/canRedo based on history state
  useEffect(() => {
    setEditorState(prev => ({
      ...prev,
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
    }));
  }, [history.past.length, history.future.length]);

  const handleMetadataSave = useCallback(async (newMetadata: ViewMetadata) => {
    try {
      // Update metadata state
      setMetadata(newMetadata);

      const updatedPageDoc = {
        ...pageDoc,
        title: newMetadata.title,
        description: newMetadata.description,
        updatedAt: new Date().toISOString(),
      };

      setPageDoc(updatedPageDoc);

      // Immediately save the metadata changes to the server
      // Don't wait for auto-save timer to prevent data loss
      setEditorState(prev => ({ ...prev, isSaving: true }));

      // Validate before saving
      const validationErrors = validatePageDoc(updatedPageDoc, newMetadata);
      if (validationErrors.length > 0) {
        console.warn('Properties save skipped due to validation errors:', validationErrors);
        setEditorState(prev => ({
          ...prev,
          isSaving: false,
          validationErrors,
          error: 'Please fix validation errors before saving'
        }));
        return;
      }

      const viewData = convertPageDocToView(updatedPageDoc, newMetadata);

      // Always update - view should always exist at this point
      // Initial view is created on mount, so this is always an update operation
      if (!viewId) {
        console.error('ViewId is missing - cannot save properties. View should have been created on mount.');
        setEditorState(prev => ({
          ...prev,
          isSaving: false,
          error: 'Unable to save: View ID is missing. Please refresh and try again.'
        }));
        return;
      }

      await viewsApiService.updateView(viewId, viewData, teamId);

      setEditorState(prev => ({
        ...prev,
        isSaving: false,
        hasUnsavedChanges: false,
        lastSaved: new Date(),
        validationErrors: [],
        success: 'Properties saved successfully'
      }));
    } catch (error) {
      console.error('Failed to save properties:', error);
      setEditorState(prev => ({
        ...prev,
        isSaving: false,
        error: 'Failed to save properties. Please try again.'
      }));
    }
  }, [pageDoc, viewId]);

  const handlePreviewToggle = useCallback(() => {
    setEditorState(prev => ({ ...prev, isPreviewMode: !prev.isPreviewMode }));
    setSelectedBlockId(undefined);
  }, []);

  const handleUndo = useCallback(() => {
    if (history.past.length === 0) {
      return;
    }

    isUndoRedoRef.current = true;
    setHistory(prev => {
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, prev.past.length - 1);

      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });
  }, [history.past.length]);

  // Effect to apply history changes to pageDoc when history.present changes
  useEffect(() => {
    if (!isUndoRedoRef.current) {
      return;
    }

    isUndoRedoRef.current = false;

    // Deep clone the entire PageDoc to ensure React detects all changes
    const restoredDoc = JSON.parse(JSON.stringify(history.present));
    restoredDoc.updatedAt = new Date().toISOString();

    setPageDoc(restoredDoc);

    setMetadata(prev => ({
      ...prev,
      title: history.present.title,
      description: history.present.description,
    }));
    setEditorState(prev => ({
      ...prev,
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
      hasUnsavedChanges: true,
    }));

    // Note: Auto-save removed - undo/redo changes persisted on manual save or before navigation
  }, [history.present, history.past.length, history.future.length]);

  const handleRedo = useCallback(() => {
    if (history.future.length === 0) {
      return;
    }

    isUndoRedoRef.current = true;
    setHistory(prev => {
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);

      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });
  }, [history.future.length]);

  const findBlockById = (doc: PageDoc, blockId: string): Block | null => {
    for (const section of doc.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          const block = column.blocks.find(b => b.id === blockId);
          if (block) return block;
        }
      }
    }
    return null;
  };

  const updateBlockInPage = (doc: PageDoc, updatedBlock: Block): PageDoc => {
    const newDoc = { ...doc };
    
    for (const section of newDoc.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          const blockIndex = column.blocks.findIndex(b => b.id === updatedBlock.id);
          if (blockIndex !== -1) {
            column.blocks[blockIndex] = updatedBlock;
            return newDoc;
          }
        }
      }
    }
    
    return newDoc;
  };

  if (editorState.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading view...</Typography>
      </Box>
    );
  }

  return (
    <ViewRenderProvider viewId={viewId || ''} pageDoc={pageDoc}>
    <Box sx={{
      height: 'calc(100vh - 64px)', // Account for AppBar height in SidebarLayout
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      // Allow wider max-width for editor so the view content area can reach standard page width (1488px)
      // Calculation: Left panel (320px) + margins (~16px) + View area (1488px) + Right panel (320px) + margins (~16px) = ~2160px
      maxWidth: '2200px',
      margin: '0 auto',
      width: '100%',
    }}>
      {/* Toolbar — hidden in readonly (view) mode. The renderer page provides
          its own reading header (title + description + kebab), so the editor
          toolbar's breadcrumb/title/status/tag block would duplicate it. */}
      {!readonly && (
        <EditorToolbar
          pageDoc={pageDoc}
          metadata={metadata}
          editorState={{
            ...editorState,
            canUndo: history.past.length > 0,
            canRedo: history.future.length > 0,
          }}
          onSave={handleSave}
          onPreviewToggle={handlePreviewToggle}
          onMetadataEdit={() => setMetadataDialogOpen(true)}
          onUndo={handleUndo}
          onRedo={handleRedo}
          readonly={readonly}
          organizationName={organizationName}
          teamName={teamName}
        />
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
        {editorState.isPreviewMode ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <PreviewRenderer pageDoc={pageDoc} />
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, height: '100%' }}>
              <MinimalDragDropEditor
                pageDoc={pageDoc}
                onPageUpdate={handlePageUpdate}
                onSave={handleSave}
                readonly={readonly}
                teamId={teamId}
                organizationId={organizationId}
              />
            </Box>
            
            {/* Property Panel (if block selected) */}
            {selectedBlockId && !readonly && (() => {
              const selectedBlock = findBlockById(pageDoc, selectedBlockId);
              if (!selectedBlock) return null;
              
              return (
                <Box sx={{ height: '100%', overflow: 'hidden' }}>
                  <PropertyPanel
                    block={selectedBlock}
                    onUpdateBlock={(updatedBlock) => {
                      const updatedDoc = updateBlockInPage(pageDoc, updatedBlock);
                      handlePageUpdate(updatedDoc);
                    }}
                    onDeleteBlock={() => {
                      // Delete block logic here
                      setSelectedBlockId(undefined);
                    }}
                    teamId={teamId}
                    organizationId={organizationId}
                  />
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* Metadata Dialog */}
      <ContentMetadataDialog
        open={metadataDialogOpen}
        onClose={() => setMetadataDialogOpen(false)}
        onSave={handleMetadataSave}
        initialValues={metadata}
        kind="view"
        contentId={viewId}
      />

      {/* Notifications */}
      <Snackbar
        open={!!editorState.error}
        autoHideDuration={6000}
        onClose={() => setEditorState(prev => ({ ...prev, error: undefined }))}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {editorState.error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!editorState.success}
        autoHideDuration={3000}
        onClose={() => setEditorState(prev => ({ ...prev, success: undefined }))}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {editorState.success}
        </Alert>
      </Snackbar>

      {/* Navigation confirmation dialog */}
      <Dialog
        open={showNavigationDialog}
        onClose={handleCancelNavigation}
        aria-labelledby="navigation-dialog-title"
        aria-describedby="navigation-dialog-description"
      >
        <DialogTitle id="navigation-dialog-title">
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="navigation-dialog-description">
            You have unsaved changes. What would you like to do?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation} color="inherit">
            Stay Here
          </Button>
          <Button onClick={handleDiscardAndNavigate} color="warning">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndNavigate} variant="contained" color="primary" autoFocus>
            Save & Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ViewRenderProvider>
  );
};

export default EnhancedPageEditor;
